// Calibrated 16-grid stroke-widths for the branded product glyphs. A flat stroke
// doesn't hold — the webview rasterises the 16-grid differently from lucide's
// 24-grid across sizes — so weight is tuned per size. Hand-tuned on-device against
// lucide in the "Product Glyphs → Branded vs Lucide" Storybook story, which
// re-reads this table for its slider defaults; re-tune there, paste the readout
// back. Shape is counterintuitive: small sizes carry MORE weight (thin strokes
// vanish, so bolden them), easing to the 1.25 base by 24px.
export const STROKE_BY_SIZE: Record<number, number> = {
  8: 1.6,
  10: 1.6,
  12: 1.4,
  14: 1.4,
  16: 1.3,
  18: 1.3,
  20: 1.25,
  24: 1.25,
  28: 1.25,
  32: 1.25,
};

// A CSS `size-N` class is invisible to the component, so those icons can't get the
// size-aware value and fall back to this — tuned to 16px, the most common CSS size.
export const FALLBACK_STROKE_WIDTH = STROKE_BY_SIZE[16] ?? 1.3;

// 16/24. Set on every branded <svg> so the DS's lucide-tuned `stroke-icon-*` CSS
// (`calc(base * var(--branded-grid-scale, 1))`, themes.ts) resolves to the 16-grid
// weight when it overrides a branded icon in a Button/Link. Lucide never sets it.
export const BRANDED_GRID_SCALE = 0.667;

const POINTS = Object.entries(STROKE_BY_SIZE)
  .map(([size, stroke]) => ({ size: Number(size), stroke }))
  .sort((a, b) => a.size - b.size);

/**
 * Calibrated 16-grid stroke for a rendered size — linear-interpolated between the
 * tuned points, clamped outside the table.
 */
export function strokeForSize(size: number): number {
  const first = POINTS[0]!;
  const last = POINTS[POINTS.length - 1]!;
  if (size <= first.size) return first.stroke;
  if (size >= last.size) return last.stroke;

  for (let i = 0; i < POINTS.length - 1; i++) {
    const lo = POINTS[i]!;
    const hi = POINTS[i + 1]!;
    if (size >= lo.size && size <= hi.size) {
      const t = (size - lo.size) / (hi.size - lo.size);
      return lo.stroke + t * (hi.stroke - lo.stroke);
    }
  }
  return last.stroke;
}

/** Shared so a glyph's outline and filled variants match in weight. */
export function resolveBrandedStrokeWidth({
  size,
  strokeWidth,
  absoluteStrokeWidth,
}: {
  size?: string | number;
  strokeWidth?: string | number;
  absoluteStrokeWidth?: boolean;
}): string | number {
  // A CSS `size-4` is invisible here, so only a passed `size` is tuned. A unit
  // size ("2rem") parses to NaN; treat it as 24.
  const sizePassed = size != null;
  const parsedSize = Number(size);
  const numericSize = sizePassed && Number.isFinite(parsedSize) ? parsedSize : 24;
  const defaultStroke = sizePassed ? strokeForSize(numericSize) : FALLBACK_STROKE_WIDTH;

  // Overrides are 24-grid, so rescale to 16; unit strings ("3px") pass through
  // as lucide does. absoluteStrokeWidth falls back to the default, never NaN.
  const numericStroke = Number(strokeWidth);
  const absoluteBase = Number.isFinite(numericStroke) ? numericStroke : defaultStroke;
  if (absoluteStrokeWidth) return (absoluteBase * 16) / numericSize;
  if (strokeWidth == null) return defaultStroke;
  return Number.isFinite(numericStroke) ? (numericStroke * 16) / 24 : strokeWidth;
}
